import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Flame, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Daily Macro Summary — NutriLens-inspired hero card showing today's totals
 * against per-macro goals with slim horizontal progress bars.
 *
 * Displays all 7 tracked macros: Calories, Protein, Carbs, Fat, Sugar, Fiber, Sodium.
 * The primary "Calories" figure is rendered large in a serif for hierarchy.
 */

function localDayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const num = (n) => {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.max(0, Math.round(Number(n)));
};

// Auto-derive macro goals from calorie_goal if user hasn't set explicit ones
// Standard macro split: 30% protein, 45% carbs, 25% fat.
function deriveGoals(user, totals) {
  const cal = user?.calorie_goal ? Number(user.calorie_goal) : null;
  const auto = (pct, kcalPerG) => (cal ? Math.round((cal * pct) / kcalPerG) : null);

  return {
    calories: cal,
    protein_g: user?.protein_goal_g ?? auto(0.3, 4),
    carbs_g: user?.carbs_goal_g ?? auto(0.45, 4),
    fat_g: user?.fat_goal_g ?? auto(0.25, 9),
    // Health defaults if not set — WHO / FDA guidance
    sugar_g: user?.sugar_goal_g ?? 50,           // upper daily added-sugar cap
    fiber_g: user?.fiber_goal_g ?? 30,           // 25-38g adult daily target
    sodium_mg: user?.sodium_goal_mg ?? 2300,     // FDA daily upper limit
  };
}

const MACROS = [
  { key: "calories",  label: "Calories", unit: "kcal", color: "#10B981", hero: true },
  { key: "protein_g", label: "Protein",  unit: "g",    color: "#E11D48" },
  { key: "carbs_g",   label: "Carbs",    unit: "g",    color: "#3B82F6" },
  { key: "fat_g",     label: "Fat",      unit: "g",    color: "#F97316" },
  { key: "sugar_g",   label: "Sugar",    unit: "g",    color: "#EC4899", inverse: true },
  { key: "fiber_g",   label: "Fiber",    unit: "g",    color: "#8B5CF6" },
  { key: "sodium_mg", label: "Sodium",   unit: "mg",   color: "#64748B", inverse: true },
];

function MacroBar({ macro, value, goal }) {
  const val = num(value);
  const g = goal ? Number(goal) : null;
  const pct = g ? Math.min(100, (val / g) * 100) : 0;
  const reached = g && val >= g;
  // For "inverse" macros (sugar, sodium), hitting or exceeding the goal is BAD
  // → color the bar amber/red instead of green.
  const barColor = macro.inverse
    ? (reached ? "#EF4444" : macro.color)
    : macro.color;

  return (
    <div
      className="border border-slate-200 bg-slate-50 px-3 py-2.5"
      data-testid={`macro-bar-${macro.key}`}
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: macro.color }}
            aria-hidden
          />
          <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-600">
            {macro.label}
          </span>
        </div>
        {reached && !macro.inverse && (
          <span
            className="font-mono-num text-[9px] uppercase tracking-widest text-emerald-400 flex items-center gap-0.5"
            data-testid={`macro-bar-${macro.key}-hit`}
          >
            <Check className="w-2.5 h-2.5" /> Hit
          </span>
        )}
        {reached && macro.inverse && (
          <span
            className="font-mono-num text-[9px] uppercase tracking-widest text-red-400"
            data-testid={`macro-bar-${macro.key}-over`}
          >
            Over
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1 mb-1.5">
        <span className="font-display font-bold text-xl leading-none text-slate-900 dark:text-white tabular-nums">
          {val.toLocaleString()}
        </span>
        {g != null && (
          <span className="text-slate-500 text-[11px] tabular-nums font-mono-num">
            / {g.toLocaleString()}
          </span>
        )}
        <span className="text-slate-400 text-[10px] font-mono-num uppercase tracking-wider ml-auto">
          {macro.unit}
        </span>
      </div>
      <div className="h-1 w-full bg-slate-50 relative overflow-hidden">
        {g != null && (
          <div
            className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
            style={{ width: `${pct}%`, background: barColor }}
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}

export default function DailyMacroSummary({ dayISO, refreshKey = 0 }) {
  const { user } = useAuth();
  const day = dayISO || localDayISO();
  const [totals, setTotals] = useState({
    calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0, sodium_mg: 0, count: 0,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/today", { params: { day } });
      setTotals(data.totals || totals);
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 500) toast.error(formatApiError(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const goals = deriveGoals(user, totals);
  const heroMacro = MACROS[0];
  const restMacros = MACROS.slice(1);

  const heroPct = goals.calories
    ? Math.min(100, (num(totals.calories) / Number(goals.calories)) * 100)
    : 0;

  return (
    <Card
      className="bg-white border border-slate-200 rounded-none p-6"
      data-testid="daily-macro-summary"
    >
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[var(--amino-teal)]">
            // Daily Summary
          </div>
          <div className="font-display font-black uppercase tracking-tight text-2xl leading-none mt-1 flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#0D9488]" /> Today's Fuel
          </div>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 text-right">
          {totals.count} entr{totals.count === 1 ? "y" : "ies"}
          <div className="text-slate-400 mt-0.5 text-[9px]">7 macros tracked</div>
        </div>
      </div>

      {/* Hero: Calories — big serif number + slim horizontal bar */}
      <div
        className="border border-slate-200 bg-slate-50 p-4 mb-3"
        data-testid="daily-macro-hero"
      >
        <div className="flex items-baseline justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: heroMacro.color }} aria-hidden />
              <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-600">
                {heroMacro.label}
              </span>
            </div>
            <div className="font-serif-stat text-5xl leading-none mt-2 text-slate-900 dark:text-white tabular-nums">
              {num(totals.calories).toLocaleString()}
              {goals.calories != null && (
                <span className="text-slate-400 text-xl font-normal ml-1 tabular-nums">
                  / {Number(goals.calories).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
              {heroMacro.unit}
            </div>
            {goals.calories != null && (
              <div className="font-mono-num text-2xl text-slate-900 dark:text-white tabular-nums mt-1">
                {Math.round(heroPct)}<span className="text-slate-500 text-sm">%</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full bg-slate-50 relative overflow-hidden">
          {goals.calories != null && (
            <div
              className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
              style={{ width: `${heroPct}%`, background: heroMacro.color }}
              aria-hidden
            />
          )}
        </div>
      </div>

      {/* Grid: remaining 6 macros */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {restMacros.map((m) => (
          <MacroBar
            key={m.key}
            macro={m}
            value={totals[m.key]}
            goal={goals[m.key]}
          />
        ))}
      </div>
    </Card>
  );
}
